/**
 * ViewModel is the base for all view-models. It holds immutable state, exposes
 * it to a View, and notifies subscribers on change. Views never mutate state
 * directly — they invoke VM commands which call `setState`.
 *
 * Framework-agnostic: React binds via `useViewModel` (see @lumora/ui), but the
 * same VM works with any renderer.
 */
import { errorMessage } from "@lumora/bridge";
export class ViewModel {
    listeners = new Set();
    _state;
    constructor(initial) {
        this._state = initial;
    }
    get state() {
        return this._state;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    setState(patch) {
        this._state = { ...this._state, ...patch };
        for (const listener of this.listeners)
            listener(this._state);
    }
    /** Standard loading/error envelope for async VM commands. */
    async run(fn, fallback, opts) {
        const flag = (opts?.flag ?? "loading");
        const start = { [flag]: true };
        if (opts?.listErrors) {
            Object.assign(start, { errors: [] });
        }
        else {
            Object.assign(start, { error: null });
        }
        this.setState(start);
        try {
            const result = await fn();
            this.setState({ [flag]: false });
            return result;
        }
        catch (err) {
            const failure = opts?.listErrors
                ? { errors: [errorMessage(err, fallback)] }
                : { error: errorMessage(err, fallback) };
            this.setState({
                [flag]: false,
                ...failure,
                ...(opts?.onError?.(err) ?? {}),
            });
            return undefined;
        }
    }
}
//# sourceMappingURL=ViewModel.js.map